import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { cardFace } from '../utils/routeBuilderModel';
import { parseDbTime, localDateFromYMD } from '../utils/dbTime';

function TimelineView({ onClose, onLoadClick, embedded = false }) {
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [viewMode, setViewMode] = useState('day'); // 'day', 'week'
  const [drivers, setDrivers] = useState([]);

  useEffect(() => {
    fetchLoads();
    fetchDrivers();
  }, [selectedDate, viewMode]);

  const fetchDrivers = async () => {
    const { data } = await supabase
      .from('drivers')
      .select('*, user:user_id(name)')
      .order('id');
    if (data) setDrivers(data);
  };

  const fetchLoads = async () => {
    setLoading(true);
    try {
      // selectedDate is 'YYYY-MM-DD'. new Date() on that is UTC midnight, so the
      // day being fetched started 4-5 hours before the day the user picked.
      const startDate = localDateFromYMD(selectedDate);
      let endDate = localDateFromYMD(selectedDate);
      
      if (viewMode === 'week') {
        endDate.setDate(endDate.getDate() + 7);
      } else {
        endDate.setDate(endDate.getDate() + 1);
      }

      const { data, error } = await supabase
        .from('loads')
        .select(`
          *,
          customer:customers(company_name),
          driver:drivers(id, phone, user_id, user:user_id(name)),
          truck:trucks(truck_number, license_plate),
          load_stops(*)
        `)
        .gte('created_at', startDate.toISOString())
        .lt('created_at', endDate.toISOString())
        .order('created_at', { ascending: true });

      if (error) throw error;
      setLoads(data || []);
    } catch (error) {
      console.error('Error fetching loads:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'var(--color-warning)',
      assigned: 'var(--accent-blue)',
      en_route: 'var(--color-info)',
      at_pickup: '#e67e22',
      loading: '#e67e22',
      in_transit: '#2ecc71',
      at_delivery: '#1abc9c',
      unloading: '#1abc9c',
      complete: 'var(--color-success)',
      cancelled: 'var(--color-danger)'
    };
    return colors[status] || '#7f8c8d';
  };

  const formatTime = (dateString) => {
    if (!dateString) return '--:--';
    return new Date(dateString).toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const getTimeSlots = () => {
    const slots = [];
    for (let hour = 0; hour < 24; hour++) {
      slots.push({
        hour,
        label: `${hour.toString().padStart(2, '0')}:00`
      });
    }
    return slots;
  };

  const getLoadPosition = (load) => {
    // loads.created_at is naive UTC: read raw, every job sat 4-5 hours to the right
    // of the hour it was actually created in.
    const createdAt = parseDbTime(load.created_at);
    if (!createdAt) return 0;
    const hour = createdAt.getHours();
    const minutes = createdAt.getMinutes();
    return (hour * 60 + minutes) / (24 * 60) * 100;
  };

  const getLoadsByDriver = () => {
    const grouped = {};
    
    // Initialize with all drivers
    drivers.forEach(driver => {
      grouped[driver.id] = {
        driver,
        loads: []
      };
    });
    
    // Add unassigned group
    grouped['unassigned'] = {
      driver: null,
      loads: []
    };
    
    // Group loads by driver
    loads.forEach(load => {
      if (load.driver_id && grouped[load.driver_id]) {
        grouped[load.driver_id].loads.push(load);
      } else {
        grouped['unassigned'].loads.push(load);
      }
    });
    
    return grouped;
  };

  const timeSlots = getTimeSlots();
  const loadsByDriver = getLoadsByDriver();

  const content = (
    <div style={{
      backgroundColor: embedded ? 'transparent' : 'var(--surface-1)',
      borderRadius: '10px',
      padding: embedded ? '0' : '1.5rem',
      width: '100%',
      maxWidth: embedded ? '100%' : '1400px',
      maxHeight: embedded ? 'none' : '90vh',
      display: 'flex',
      flexDirection: 'column',
      overflow: embedded ? 'visible' : 'hidden'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1rem',
        paddingBottom: '1rem',
        borderBottom: '1px solid var(--border-default)',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div>
          <h2 style={{ color: 'var(--accent-primary)', margin: '0 0 0.25rem 0' }}>
            Timeline View
          </h2>
          <p style={{ color: 'var(--text-muted)', margin: 0, fontSize: '0.9rem' }}>
            Chronological schedule of all loads
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          {/* Date picker */}
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            style={{
              padding: '0.5rem',
              backgroundColor: 'var(--surface-2)',
              border: '1px solid var(--border-default)',
              borderRadius: '5px',
              color: 'var(--text-primary)',
              cursor: 'pointer'
            }}
          />
          
          {/* View mode toggle */}
          <div style={{
            display: 'flex',
            backgroundColor: 'var(--surface-2)',
            borderRadius: '5px',
            overflow: 'hidden'
          }}>
            <button
              onClick={() => setViewMode('day')}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: viewMode === 'day' ? 'var(--btn-primary-bg)' : 'transparent',
                color: 'var(--text-primary)',
                border: 'none',
                cursor: 'pointer',
                fontSize: '0.85rem'
              }}
            >
              Day
            </button>
            <button
              onClick={() => setViewMode('week')}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: viewMode === 'week' ? 'var(--btn-primary-bg)' : 'transparent',
                color: 'var(--text-primary)',
                border: 'none',
                cursor: 'pointer',
                fontSize: '0.85rem'
              }}
            >
              Week
            </button>
          </div>
          
          {!embedded && (
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--text-primary)',
                fontSize: '1.5rem',
                cursor: 'pointer'
              }}
            >
              x
            </button>
          )}
        </div>
      </div>

        {/* Timeline Content */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
              Loading timeline...
            </div>
          ) : (
            <div style={{ minWidth: '1200px' }}>
              {/* Time header */}
              <div style={{
                display: 'flex',
                borderBottom: '1px solid var(--border-default)',
                position: 'sticky',
                top: 0,
                backgroundColor: 'var(--surface-1)',
                zIndex: 10
              }}>
                <div style={{
                  width: '150px',
                  flexShrink: 0,
                  padding: '0.5rem',
                  borderRight: '1px solid var(--border-default)',
                  color: 'var(--accent-primary)',
                  fontWeight: 'bold'
                }}>
                  Driver
                </div>
                <div style={{ flex: 1, display: 'flex' }}>
                  {timeSlots.filter((_, i) => i % 2 === 0).map(slot => (
                    <div
                      key={slot.hour}
                      style={{
                        flex: 1,
                        padding: '0.5rem',
                        textAlign: 'center',
                        color: 'var(--text-muted)',
                        fontSize: '0.8rem',
                        borderRight: '1px solid var(--border-default)'
                      }}
                    >
                      {slot.label}
                    </div>
                  ))}
                </div>
              </div>

              {/* Driver rows */}
              {Object.entries(loadsByDriver).map(([driverId, { driver, loads: driverLoads }]) => (
                <div
                  key={driverId}
                  style={{
                    display: 'flex',
                    borderBottom: '1px solid var(--border-default)',
                    minHeight: '60px'
                  }}
                >
                  {/* Driver name */}
                  <div style={{
                    width: '150px',
                    flexShrink: 0,
                    padding: '0.5rem',
                    borderRight: '1px solid var(--border-default)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <span style={{ fontSize: '1.2rem' }}>
                      {driver ? '👤' : '📋'}
                    </span>
                    <div>
                      <div style={{ color: 'var(--text-primary)', fontSize: '0.9rem' }}>
                        {driver?.user?.name || driver?.phone || 'Unassigned'}
                      </div>
                      {driver && (
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                          {driverLoads.length} load{driverLoads.length !== 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Timeline area */}
                  <div style={{
                    flex: 1,
                    position: 'relative',
                    backgroundColor: 'var(--bg-primary)'
                  }}>
                    {/* Hour grid lines */}
                    {timeSlots.filter((_, i) => i % 2 === 0).map(slot => (
                      <div
                        key={slot.hour}
                        style={{
                          position: 'absolute',
                          left: `${(slot.hour / 24) * 100}%`,
                          top: 0,
                          bottom: 0,
                          width: '1px',
                          backgroundColor: 'var(--surface-2)'
                        }}
                      />
                    ))}

                    {/* Load blocks */}
                    {driverLoads.map((load, index) => {
                      const position = getLoadPosition(load);
                      const pickups = (load.load_stops || []).filter(s => s.stop_type === 'pickup');
                      const deliveries = (load.load_stops || []).filter(s => s.stop_type === 'delivery');
                      
                      return (
                        <div
                          key={load.id}
                          onClick={() => onLoadClick && onLoadClick(load)}
                          style={{
                            position: 'absolute',
                            left: `${position}%`,
                            top: `${5 + (index % 2) * 28}px`,
                            width: '120px',
                            padding: '0.25rem 0.5rem',
                            backgroundColor: getStatusColor(load.status),
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                            color: 'var(--text-primary)',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                            zIndex: 5
                          }}
                          title={`${cardFace(load).tooltip}\n${load.commodity || 'No commodity'}\nStatus: ${load.status}`}
                        >
                          {/* __CARD_FACE__ customer, then From -> To (the load number is in the hover text / opened job) */}
                          <div style={{ fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cardFace(load).customer}</div>
                          <div style={{ opacity: 0.9, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {cardFace(load).route}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* Empty state */}
              {loads.length === 0 && (
                <div style={{
                  textAlign: 'center',
                  padding: '3rem',
                  color: 'var(--text-muted)'
                }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📅</div>
                  <p>No loads scheduled for this {viewMode === 'day' ? 'day' : 'week'}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Legend */}
        <div style={{
          marginTop: '1rem',
          paddingTop: '1rem',
          borderTop: '1px solid var(--border-default)',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '1rem',
          justifyContent: 'center'
        }}>
          {[
            { status: 'pending', label: 'Pending' },
            { status: 'assigned', label: 'Assigned' },
            { status: 'en_route', label: 'En Route' },
            { status: 'in_transit', label: 'In Transit' },
            { status: 'complete', label: 'Complete' }
          ].map(({ status, label }) => (
            <div key={status} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '2px',
                backgroundColor: getStatusColor(status)
              }} />
              <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{label}</span>
            </div>
          ))}
        </div>
      </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      {content}
    </div>
  );
}

export default TimelineView;
